import React, { useState, useEffect } from 'react';
import styles from './SurveySubmissions.module.scss';
import Table from '../../../components/table/Table';
import PageTitle from '../../../components/pageTitle/PageTitle';
import Breadcrumbs from '../../../components/breadcrumbs/Breadcrumbs';
import Modal from '../../../components/modal/Modal';
import Button from '../../../components/button/Button';
import Icon from '../../../components/icon/Icon';
import { useParams } from 'react-router-dom';
import axiosClient from '../../../api/axiosClient';
import { useToasts } from '../../../contexts/ToastContext';
import LoadingOverlay from '../../../components/loadingOverlay/LoadingOverlay';

const SurveySubmissions = () => {
  const { showToast } = useToasts();
  const { surveyId } = useParams();
  const [data, setData] = useState([]);
  const [surveyTitle, setSurveyTitle] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1, total: 0, start: 1, end: 1, hasPrev: false, hasNext: false
  });

  const fetchSubmissions = React.useCallback(async (page = 1) => {
    setIsLoading(true);
    try {
      const params = { page, limit: 10 };
      if (surveyId) params.surveyId = surveyId;
      const response = await axiosClient.get('/surveys/submissions', { params });
      const responseData = response.data;
      setSurveyTitle(responseData.survey?.title || '');
      setData(responseData.submissions || []);
      const totalPages = Math.ceil(responseData.total / responseData.limit);
      setPagination({
        current: responseData.page,
        total: responseData.total,
        start: (responseData.page - 1) * responseData.limit + 1,
        end: Math.min(responseData.page * responseData.limit, responseData.total),
        hasPrev: responseData.page > 1,
        hasNext: responseData.page < totalPages
      });
    } catch (error) {
      showToast(error.message || 'Failed to fetch submissions', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [showToast, surveyId]);

  useEffect(() => {
    fetchSubmissions(1);
  }, [fetchSubmissions]);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const response = await axiosClient.post('/reports/survey-export', { surveyId }, {
        responseType: 'blob',
        transformResponse: [(data) => data],
      });
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'survey-submissions.xlsx';
      link.click();
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setIsExporting(false);
    }
  };

  const handleRowClick = async (row) => {
    setIsDetailLoading(true);
    setSelectedSubmission({});
    try {
      const response = await axiosClient.get(`/surveys/submissions/${row.id}`);
      setSelectedSubmission(response.data?.submission || response.data || response);
    } catch (error) {
      showToast(error.message || 'Failed to fetch submission details', 'error');
      setSelectedSubmission(null);
    } finally {
      setIsDetailLoading(false);
    }
  };

  const columns = [
    {
      header: 'Related To',
      key: 'relatedTo',
      render: (row) => {
        const label = row.instructor
          ? `${row.instructor.firstName || ''} ${row.instructor.lastName || ''}`.trim()
          : row.clinicalSite?.name || '—';
        return (
          <span className={styles.titleLink} onClick={() => handleRowClick(row)}>
            {label}
          </span>
        );
      },
    },
    { header: 'Total Score', key: 'totalScore' },
    {
      header: 'Submitted At',
      key: 'submittedAt',
      render: (row) => new Date(row.submittedAt).toLocaleDateString(),
    },
  ];

  const contextLabel = selectedSubmission?.instructor
    ? `${selectedSubmission.instructor.firstName || ''} ${selectedSubmission.instructor.lastName || ''}`.trim()
    : selectedSubmission?.clinicalSite?.name || '';

  return (
    <>
      {isLoading && <LoadingOverlay />}
      <div className={styles.breadcrumbs}>
        <Breadcrumbs
          breadcrumbs={[
            { label: 'Surveys', path: '/surveys' },
            { label: 'Submissions' },
          ]}
        />
      </div>
      <div className={styles.header}>
        <PageTitle label={surveyTitle ? `${surveyTitle} - Submissions` : 'Submissions'} />
        <Button
          label={
            <div className={styles.cta}>
              <Icon name="arrow-up-right" />
              {isExporting ? 'Exporting...' : 'Export'}
            </div>
          }
          variant="primary-light"
          size="medium"
          onClick={handleExport}
          disabled={isExporting}
        />
      </div>
      <div className={styles.container}>
        <Table
          columns={columns}
          data={data}
          pagination={pagination}
          onPageChange={(page) => fetchSubmissions(page)}
          emptyMessage="No submissions found."
        />
      </div>

      {selectedSubmission !== null && (
        <Modal>
          <div className={styles.modal}>
            {isDetailLoading && <LoadingOverlay />}
            <div className={styles.modalHeader}>
              <div>
                <h2 className={styles.modalTitle}>{contextLabel}</h2>
                {selectedSubmission.submittedAt && (
                  <p className={styles.modalMeta}>
                    Submitted: {new Date(selectedSubmission.submittedAt).toLocaleDateString()}
                    <span className={styles.score}>Total Score: {selectedSubmission.totalScore}</span>
                  </p>
                )}
              </div>
              <button className={styles.closeBtn} onClick={() => setSelectedSubmission(null)}>✕</button>
            </div>

            {selectedSubmission.answers?.length > 0 && (
              <div className={styles.answers}>
                {selectedSubmission.answers.map((answer, index) => (
                  <div key={answer.questionId} className={styles.answerRow}>
                    <p className={styles.questionText}>
                      <span className={styles.questionNumber}>{index + 1}.</span>
                      {answer.questionText}
                    </p>
                    <div className={styles.answerValue}>
                      <span className={styles.optionValue}>{answer.optionValue}</span>
                      <span className={styles.optionScore}>Score: {answer.score}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className={styles.modalActions}>
              <Button label="Close" variant="white" size="small" onClick={() => setSelectedSubmission(null)} />
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default SurveySubmissions;
